// Pure decision logic for the post-login 2FA hand-off (ACA-250 §5.1-§5.2, §5.3 R3). No imports.

const ALLOWED_HOSTS = ["artcloud.com", "artcloud.market", "artlogic.net"];

// https only, host == or subdomain of an allowed host - tighter than the bare endsWith it replaces.
export function safeReturnUrl(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }

  if (url.protocol !== "https:") {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  const allowed = ALLOWED_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));

  return allowed ? raw : null;
}

export interface ShouldPromptTwoFactorArgs {
  branded: boolean;
  rebrand: boolean;
  challenged: boolean;
}

// The login API challenges exactly the users with an active factor, so an unchallenged login is an unenrolled user.
export function shouldPromptTwoFactor({ branded, rebrand, challenged }: ShouldPromptTwoFactorArgs): boolean {
  return branded && rebrand && !challenged;
}

export interface PostLoginArgs {
  // Null for a login path that is not the sign-in form (the pre-2FA callers), which never prompts.
  login: { branded: boolean; challenged: boolean } | null | undefined;
  rebrand: boolean;
  isCollector: boolean;
  href: string;
  pathname: string;
  hash: string;
  returnUrl: string | null;
  mktBaseUrl: string | null;
  proBaseUrl: string | null;
  // The setup page needs the session cookie just set, which another domain (artcloud.market) can't read.
  pageBaseUrl: string;
}

// Where _postlogin sends the browser; null means reload the current page.
export function postLoginNavigation(args: PostLoginArgs): string | null {
  const onLoginOrReset = args.pathname.indexOf("/login") > -1 || args.hash === "#reset";
  const redirectUrl = onLoginOrReset
    ? safeReturnUrl(args.returnUrl) || (args.isCollector ? args.mktBaseUrl : args.proBaseUrl)
    : null;

  const { login } = args;
  if (login && shouldPromptTwoFactor({ branded: login.branded, rebrand: args.rebrand, challenged: login.challenged })) {
    const destination = onLoginOrReset ? redirectUrl : args.href;
    return `${args.pageBaseUrl}login/two-factor?returnUrl=${encodeURIComponent(destination ?? "")}`;
  }

  return redirectUrl || null;
}
